/*
  Based on ChevronDown/ChevronUp from Feather Icons, and redraws itself nicely when flipping from down to up. Meant to be used for expand/collapse UIs.
*/
import { styled } from '@linaria/react';
import { useSpring, animated } from 'react-spring';

import useBoopMinimal from '@/hooks/use-boop-minimal';
import usePrefersReducedMotion from '@/hooks/use-prefers-reduced-motion';

const SPRING_CONFIG = {
  tension: 300,
  friction: 18,
};

interface Props extends React.SVGProps<SVGSVGElement> {
  size?: number;
  isHovering?: boolean;
  direction?: 'down' | 'up';
}

function IconChevron({
  size = 20,
  isHovering = false,
  direction = 'down',
  ...delegated
}: Props) {
  const isBooped = useBoopMinimal(isHovering);
  const prefersReducedMotion = usePrefersReducedMotion();

  // The line essentially has three states:
  // • Closed, where the arrow points down.
  // • Open, where the arrow points up.
  // • Booped, where the arrow points a tiny bit more in the current direction, and scrunches in a bit.
  const polyLineProps = useSpring({
    points:
      direction === 'down'
        ? isBooped
          ? '7 10 12 17 17 10'
          : '6 9 12 15 18 9'
        : isBooped
          ? '7 15 12 7 17 15'
          : '6 15 12 9 18 15',
    config: SPRING_CONFIG,
    immediate: prefersReducedMotion,
  });

  return (
    <Svg
      xmlns="http://www.w3.org/2000/svg"
      width={size / 16 + 'rem'}
      height={size / 16 + 'rem'}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...delegated}
    >
      <animated.polyline {...polyLineProps} />
    </Svg>
  );
}

const Svg = styled.svg`
  display: block;
  overflow: visible;
`;

export default IconChevron;
